// The `/app` index: where a sign-in lands, chosen by plan.
//
// Pulse for Premium; the Profile for Basic, because Pulse is behind the
// upgrade card and a Basic restaurant greeted by it on every visit is being
// nagged, not sold (decided 2026-09-08). The plan is the server's answer
// (`useBilling`), which needs the signer up and a server round-trip — several
// seconds after a sign-in. So this waits while that is genuinely in progress,
// uses the last answer it saw for this restaurant to decide at once on a
// return visit, and falls through to Pulse only when the plan cannot be had.
// (A fixed three-second deadline shipped first, and a Basic restaurant still
// landed on Pulse: the signer alone takes longer than that.)

import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useBilling, readLastKnownPlan } from '@/state/useBilling'
import { useTeam } from '@/state/useTeam'
import { useLoyaltyContext } from '@/hooks/useLoyaltyContext'
import { BASIC_LANDING_PATH, PREMIUM_LANDING_PATH } from './navRegistry'

/** The cap on waiting for the signer and the server, not the normal path. */
const SAFETY_DEADLINE_MS = 20_000

function pathFor(plan: 'basic' | 'premium'): string {
  return plan === 'basic' ? BASIC_LANDING_PATH : PREMIUM_LANDING_PATH
}

export function LandingRedirect() {
  const ctx = useLoyaltyContext()
  const { restaurantPubkey, bunkerConnected, error: teamError } = useTeam()
  const { loaded, loading, error, plan, refresh } = useBilling()
  const [deadline, setDeadline] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDeadline(true), SAFETY_DEADLINE_MS)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (ctx && !loaded && !loading && !error) void refresh(ctx)
  }, [ctx, loaded, loading, error, refresh])

  if (loaded) {
    return <Navigate to={pathFor(plan)} replace />
  }

  // A return visit: decide from the last answer the server gave for this
  // restaurant. The fresh answer keeps loading behind the page for the gates.
  const remembered = restaurantPubkey ? readLastKnownPlan(restaurantPubkey) : null
  if (remembered) {
    return <Navigate to={pathFor(remembered)} replace />
  }

  // Nothing will arrive: the signer failed, the server refused, or we have
  // waited past reason. Pulse is what every restaurant saw before.
  const signerStalled = Boolean(restaurantPubkey) && !bunkerConnected && Boolean(teamError)
  if (error || signerStalled || deadline) {
    return <Navigate to={PREMIUM_LANDING_PATH} replace />
  }

  return (
    <div className="flex h-64 items-center justify-center" data-testid="landing-loading">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  )
}
